import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Box, ButtonBase, CircularProgress, IconButton, Typography } from "@mui/material";
import type { FC } from "react";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { FONT_SIZE, INK, NEUTRAL } from "@/theme/tokens";
import { JumpingDots } from "../Animations/JumpingDots";
import SystemButton from "../SystemButton/SystemButton";
import { TOUCH_TARGET_SX } from "../touchTarget";

/** One selectable conversation in the rail. */
export type ChatSessionRailItem = {
	id: string;
	title: string;
};

type Props = {
	items: ChatSessionRailItem[] | undefined;
	activeId: string;
	/** Id currently being deleted — shows a spinner on its row and disables every delete button. */
	deletingId: string | null;
	isLoading: boolean;
	isError: boolean;
	onNew: () => void;
	onSelect: (id: string) => void;
	onDelete: (id: string) => void;
	/** Already-translated copy (no i18n inside the primitive). */
	newLabel: string;
	emptyLabel: string;
	errorLabel: string;
	deleteAriaLabel: string;
	dataTestId?: string;
	/** Test id for the "new chat" button. */
	newButtonDataTestId?: string;
	/** Per-row test id for the select affordance. */
	getItemDataTestId?: (id: string) => string;
	/** Per-row test id for the delete button. */
	getDeleteDataTestId?: (id: string) => string;
};

const DELETE_ICON_SIZE = 18;

/**
 * A collapsible chat session rail: a sticky "new chat" header over a scrollable
 * list of past conversations (switch on click, per-row delete with an in-flight
 * spinner, active-row highlight, pointer-only hover). Generic over any
 * `{id, title}` list; all copy arrives as already-translated props.
 */
export const ChatSessionRail: FC<Props> = ({
	items,
	activeId,
	deletingId,
	isLoading,
	isError,
	onNew,
	onSelect,
	onDelete,
	newLabel,
	emptyLabel,
	errorLabel,
	deleteAriaLabel,
	dataTestId,
	newButtonDataTestId,
	getItemDataTestId,
	getDeleteDataTestId,
}) => (
	<Box data-testid={dataTestId} sx={{ display: "flex", flexDirection: "column", height: "100%", minWidth: 0 }}>
		<Box sx={{ p: 1.5, borderBottom: `1px solid ${NEUTRAL[200]}`, flexShrink: 0 }}>
			{/* `touchTarget` because this is a standalone header control, not a row-aligned one — the prop
			    is opt-in precisely so it cannot clamp `CONTROL_HEIGHT.STANDARD` rows elsewhere. */}
			<SystemButton
				buttonVariant="secondary"
				size="small"
				leadingIcon={<AddIcon />}
				onClick={onNew}
				data-testid={newButtonDataTestId}
				noFullWidthMobile
				touchTarget
			>
				{newLabel}
			</SystemButton>
		</Box>
		<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", py: 0.5 }}>
			{isLoading && (
				<Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
					<JumpingDots />
				</Box>
			)}
			{isError && <Typography sx={{ px: 1.5, py: 1, fontSize: FONT_SIZE.SECONDARY, color: "error.main" }}>{errorLabel}</Typography>}
			{!isLoading && !isError && !items?.length && (
				<Typography sx={{ px: 1.5, py: 1, fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>{emptyLabel}</Typography>
			)}
			{items?.map((item) => {
				const isActive = item.id === activeId;
				const isDeleting = item.id === deletingId;
				return (
					<Box
						key={item.id}
						sx={{
							display: "flex",
							alignItems: "center",
							minWidth: 0,
							mx: 0.5,
							borderRadius: 1.5,
							backgroundColor: isActive ? NEUTRAL[150] : "transparent",
							"@media (hover: hover) and (pointer: fine)": { "&:hover": { backgroundColor: isActive ? NEUTRAL[150] : NEUTRAL[100] } },
						}}
					>
						<InHouseTooltip title={item.title} placement="right">
							<ButtonBase
								data-testid={getItemDataTestId?.(item.id)}
								onClick={() => onSelect(item.id)}
								aria-current={isActive}
								sx={[{ flex: 1, minWidth: 0, justifyContent: "flex-start", textAlign: "start", px: 1, py: 0.75, borderRadius: 1.5 }, TOUCH_TARGET_SX]}
							>
								<Typography
									sx={{
										fontSize: FONT_SIZE.SECONDARY,
										fontWeight: isActive ? 600 : 400,
										color: INK.PRIMARY,
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
								>
									{item.title}
								</Typography>
							</ButtonBase>
						</InHouseTooltip>
						<IconButton
							data-testid={getDeleteDataTestId?.(item.id)}
							aria-label={deleteAriaLabel}
							size="small"
							disabled={deletingId !== null}
							onClick={() => onDelete(item.id)}
							sx={[{ flexShrink: 0, mr: 0.25, color: INK.MUTED }, TOUCH_TARGET_SX]}
						>
							{isDeleting ? <CircularProgress size={DELETE_ICON_SIZE} /> : <DeleteOutlineIcon sx={{ fontSize: DELETE_ICON_SIZE }} />}
						</IconButton>
					</Box>
				);
			})}
		</Box>
	</Box>
);
